// @ts-strict-ignore
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import { Avatar } from '@node-space/ui-components'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { useFormFields } from 'hooks/forms/useFormFields'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useCreateChannel } from 'hooks/mutations/useCreateChannel'
import { useCryptoCurrenciesQuery } from 'hooks/queries/useCryptoCurrenciesQuery'
import { useFiatCurrenciesQuery } from 'hooks/queries/useFiatCurrenciesQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { getContactComplianceDetails } from 'pages/Contacts/utils/utils'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ChannelCreateRequest, ChannelPaymentStep, PartyDetailsType } from 'types/payments'
import { Option } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { generateReference, mapToOptions } from 'utils/utils'
import { channelPaymentStepAtom, channelPaymentUrlAtom } from '../atoms/atoms'
import { ChannelPaymentFormKeys, useChannelPaymentSchema } from '../schemas/channelPaymentSchema'

export const useChannelPaymentForm = () => {
  const { t } = useTranslation()
  const { getSupportedCurrencies, getSupportedCurrencyWallets, getWalletOptions } =
    useSupportedCurrencies()
  const [walletAction] = useContext(WalletActionContext)
  const formSchema = useChannelPaymentSchema(t)
  const contact = useAtomValue(contactAtom)
  const setChannelPaymentLinkStep = useSetAtom(channelPaymentStepAtom)
  const setChannelPaymentUrl = useSetAtom(channelPaymentUrlAtom)
  const { data: fiatCurrencies, isFetching: isFetchingFiatCurrencies } = useFiatCurrenciesQuery()
  const { data: cryptoCurrencies, isFetching: isFetchingCryptoCurrencies } =
    useCryptoCurrenciesQuery()
  const { mutate, isPending } = useCreateChannel()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const [searchWalletQuery, setSearchWalletQuery] = useState('')
  const debouncedSearchTerm = useDebounce(searchWalletQuery, SEARCH_INPUT_DEBOUNCE_TIME)
  const { data: wallets, isFetching: isFetchingWallets } = useWalletsQueryV2({
    walletType: '',
    searchTerm: debouncedSearchTerm,
    max: 20,
    order: 'asc',
    sort: 'description',
  })

  const activeWallets = getSupportedCurrencyWallets(wallets?.data)
  const [activeWallet, setActiveWallet] = useState(null)
  const [walletError, setWalletError] = useState(false)
  const fiatCurrencyOptions = mapToOptions(fiatCurrencies)
  const filteredCryptoCurrencies = getSupportedCurrencies(cryptoCurrencies)
  const cryptoCurrenyOptions = mapToOptions(filteredCryptoCurrencies)
  const isFetchingCurrencies = isFetchingCryptoCurrencies || isFetchingFiatCurrencies

  useEffect(() => {
    if (walletAction?.meta?.sourceWallet && wallets?.data && !formValues?.walletId) {
      const sourceWallet = wallets?.data?.find(
        wallet => wallet?.lsid === walletAction?.meta?.sourceWallet?.lsid
      )
      if (!sourceWallet) {
        wallets?.data?.unshift(walletAction?.meta?.sourceWallet)
        setActiveWallet(walletAction?.meta?.sourceWallet)
        setValue('walletId', walletAction?.meta?.sourceWallet?.lsid, {
          shouldValidate: true,
          shouldDirty: true,
        })
        setValue('displayCurrency', walletAction?.meta?.sourceWallet?.currency?.code, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }

      if (sourceWallet) {
        setActiveWallet(sourceWallet)
        setValue('walletId', sourceWallet?.lsid, {
          shouldValidate: true,
          shouldDirty: true,
        })
        setValue('displayCurrency', sourceWallet?.currency?.code, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
    }
  }, [walletAction, wallets])

  const mapOption = (option: Partial<Option>) => ({
    ...option,
    leftText: option?.label,
    LeftElement: <Avatar name={option?.value?.toLowerCase()} size="small" />,
  })

  const mappedCryptoCurrencyOptions = cryptoCurrenyOptions?.map(mapOption)

  const currenciesOptions = [
    ...fiatCurrencyOptions.map(mapOption),
    ...mappedCryptoCurrencyOptions,
  ].sort((x, y) => x?.label?.localeCompare(y?.label))

  const cryptoCurrenciesOptionsMapped = [...mappedCryptoCurrencyOptions]?.sort((x, y) =>
    x?.label?.localeCompare(y?.label)
  )

  const activeWalletOptions = useMemo(() => getWalletOptions(activeWallets), [activeWallets])

  const form = useForm<ChannelCreateRequest>({
    defaultValues: {
      walletId: '',
      displayCurrency: '',
      payCurrency: '',
      reference: `${generateReference()}`,
    },
    resolver: yupFormResolver<ChannelCreateRequest>(formSchema),
  })

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = form

  const formValues = useWatch({ control })

  const { setInputField, setBaseField } = useFormFields<ChannelPaymentFormKeys>(
    formSchema,
    form,
    formValues
  )

  const formFields: { [key in ChannelPaymentFormKeys] } = {
    walletId: setBaseField({
      key: 'walletId',
      placeholder: t('select'),
    }),
    displayCurrency: setBaseField({
      key: 'displayCurrency',
      placeholder: t('select'),
    }),
    payCurrency: setBaseField({
      key: 'payCurrency',
      placeholder: t('select'),
    }),
    reference: setInputField({
      key: 'reference',
    }),
  }

  const onSubmit: SubmitHandler<ChannelCreateRequest> = values => {
    if (!values.walletId) {
      setWalletError(true)
      return
    }

    const data: ChannelCreateRequest = {
      ...values,
      customerId: contact?.id,
      complianceDetails: getContactComplianceDetails(contact, PartyDetailsType.ORIGINATOR),
    }
    resetRequestError()
    setWalletError(false)
    mutate(data, {
      onSuccess: res => {
        track.Amp.track(AmplitudeEvent.EOF_CHANNEL, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.CLICK,
        })
        setChannelPaymentUrl(res?.redirectUrl)
        setChannelPaymentLinkStep(ChannelPaymentStep.COMPLETE)
      },
      onError: (error: BaseErrorResponse, data: ChannelCreateRequest) => {
        track.Amp.track(AmplitudeEvent.EOF_CHANNEL_ERROR, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.ERROR,
        })
        const errorDetails = error?.data?.details?.errors ?? null
        const errorStatus = error?.status
        const errorList = error?.data?.errorList
        if (errorList?.length) {
          errorList?.forEach(axiosErr =>
            setError(axiosErr.parameter as keyof ChannelCreateRequest, {
              message: axiosErr?.message,
            })
          )
          return
        }

        const requestError = error?.data?.message || t('oopsSomethingWentWrongContracted')
        const sentryTraceId = logSentryErrorAndGetTraceId(
          `Failed API request to /v2/channel with status ${error?.status} - useCreateChannel`,
          error,
          {
            data: typeof data === 'string' ? { message: data } : data,
          }
        )

        setRequestError({
          handledMessage: requestError,
          sentryTraceId,
          show: true,
          errorCode: errorStatus,
          ErrorMessages: errorDetails,
        })
      },
    })
  }

  const handleWalletClose = () => {
    setSearchWalletQuery('')
  }

  const handleWalletOpen = () => {
    if (!activeWallets || activeWallets.length === 0) {
      setWalletError(true)
    } else {
      setWalletError(false)
    }
  }

  const handleOnChangeWallet = useCallback(
    (walletId: string) => {
      const selectedWallet = activeWallets?.find(activeWallet => {
        return activeWallet?.lsid == walletId
      })

      if (selectedWallet) {
        setActiveWallet(selectedWallet)
        setValue('walletId', selectedWallet?.lsid, { shouldValidate: true, shouldDirty: true })
        setValue('displayCurrency', selectedWallet?.currency?.code, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
      setWalletError(false)
      setSearchWalletQuery('')
    },
    [activeWallets]
  )

  const hasCurrencyMismatch =
    formValues?.walletId &&
    formValues?.displayCurrency &&
    formValues?.displayCurrency !== activeWallet?.currency?.code

  return {
    activeWalletOptions,
    currenciesOptions,
    cryptoCurrenciesOptionsMapped,
    isFetchingWallets,
    isFetchingCurrencies,
    errors,
    requestError,
    isPending,
    walletError,
    formFields,
    hasCurrencyMismatch,
    activeWallet,
    handleWalletOpen,
    handleWalletClose,
    setSearchWalletQuery,
    watch,
    setValue,
    handleOnChangeWallet,
    handleSubmit,
    onSubmit,
  }
}
